import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { creators as ProjectPerformanceActions } from '../../../Reducers/Reporting/ProjectPerformance';
import { creators as ReportSettingActions } from '../../../Reducers/Reporting/ReportSetting';
import TopChart from './TopChart';
import BottomChart from './BottomChart';

const arraysEqual = (left = [], right = []) => {
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;
  return left.every((item, index) => item === right[index]);
};

const ProjectPerformance = ({
  defaultYear,
  defaultDimDate,
  selectedCompany,
  defaultStartMonth,
  projectPerformanceSetting,
  bubbleFlag,
}) => {
  const dispatch = useDispatch();
  const projectPerformanceSummary = useSelector((state) => state.Reporting.Summary.reportingSummaries?.projectPerformance ?? '');
  const summaryData = useSelector((state) => state.Reporting.ProjectPerformance.summaryData);
  const detailData = useSelector((state) => state.Reporting.ProjectPerformance.detailData);
  const lastFetchedCompany = useSelector(
    (state) => state?.Reporting?.Default?.defaultCompany ?? null,
  );
  const lastFetchedDimDate = useSelector(
    (state) => state?.Reporting?.Default?.defaultDimDate ?? null,
  );
  const isMounted = useRef(false);

  const [selectedYears, setSelectedYears] = useState([defaultYear]);
  const [label, setLabel] = useState('');
  const [profitType, setProfitType] = useState('Director');
  const [profitSubType, setProfitSubType] = useState('YTD');
  const [selectedTopItems, setSelectedTopItems] = useState([]);
  const [topchartHeight] = useState(300);
  const [showFilter, setShowFilter] = useState('showall');
  const [sortFilter, setSortFilter] = useState('sortbyprofit');

  const getProjectPerformanceSummary = useCallback((selectedYear, DateKey = null) => {
    dispatch(ProjectPerformanceActions.projectperformanceSummaryRequest(selectedYear, DateKey));
  }, [dispatch]);

  const getProjectPerformanceDetail = useCallback((selectedYear, DateKey = null) => {
    dispatch(ProjectPerformanceActions.projectperformanceDetailRequest(selectedYear, DateKey));
  }, [dispatch]);

  const reportSettingUpdateRequest = useCallback((update_setting) => {
    dispatch(ReportSettingActions.reportSettingUpdateRequest(update_setting));
  }, [dispatch]);

  const handleFilter = useCallback(async (event = {}) => {
    const nextProfitType = event.profitType || profitType;
    const nextProfitSubType = event.profitSubType || profitSubType;
    const nextShowFilter = event.showFilter || showFilter;
    const nextSortFilter = event.sortFilter || sortFilter;
    const hasSelectedTopItems = Object.prototype.hasOwnProperty.call(event, 'selectedTopItems');
    const nextSelectedTopItems = hasSelectedTopItems
      ? event.selectedTopItems || []
      : selectedTopItems;

    const settingChanged =
      nextProfitType !== profitType ||
      nextProfitSubType !== profitSubType ||
      nextShowFilter !== showFilter ||
      nextSortFilter !== sortFilter ||
      (hasSelectedTopItems && !arraysEqual(selectedTopItems, nextSelectedTopItems));

    if (settingChanged) {
      await reportSettingUpdateRequest({
        ProjectPerformance: JSON.stringify({
          profitType: nextProfitType,
          profitSubType: nextProfitSubType,
          showFilter: nextShowFilter,
          sortFilter: nextSortFilter,
          selectedTopItems: nextSelectedTopItems,
        }),
      });
    }

    if (hasSelectedTopItems && !arraysEqual(selectedTopItems, nextSelectedTopItems)) {
      setSelectedTopItems(nextSelectedTopItems);
    }

    if (event.profitType && event.profitType !== profitType) setProfitType(event.profitType);
    if (event.profitSubType && event.profitSubType !== profitSubType) setProfitSubType(event.profitSubType);
    if (event.showFilter && event.showFilter !== showFilter) setShowFilter(event.showFilter);
    if (event.sortFilter && event.sortFilter !== sortFilter) setSortFilter(event.sortFilter);

    if (event.selectedYears) {
      const nextSelectedYears = Array.isArray(event.selectedYears)
        ? event.selectedYears
        : [event.selectedYears];
      if (!arraysEqual(selectedYears, nextSelectedYears)) {
        setSelectedYears(nextSelectedYears);
      }
      if (event.label && event.label !== label) setLabel(event.label);
    }
  }, [
    label,
    profitType,
    profitSubType,
    reportSettingUpdateRequest,
    selectedTopItems,
    selectedYears,
    showFilter,
    sortFilter,
  ]);

  useEffect(() => {
    isMounted.current = true;
    setSelectedYears([defaultYear]);
    setLabel(defaultYear.toString());

    if (projectPerformanceSetting) {
      const settings = JSON.parse(projectPerformanceSetting);
      setProfitType(settings.profitType);
      setProfitSubType(settings.profitSubType);
      setShowFilter(settings.showFilter);
      setSortFilter(settings.sortFilter);
      setSelectedTopItems(settings.selectedTopItems || []);
    } else {
      reportSettingUpdateRequest({
        ProjectPerformance: JSON.stringify({
          profitType: 'Director',
          profitSubType: 'YTD',
          showFilter: 'showall',
          sortFilter: 'sortbyprofit',
          selectedTopItems: [],
        }),
      });
    }

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      setSelectedYears([defaultYear]);
      setLabel(defaultYear.toString());
    }
  }, [defaultYear]);

  const triggerProjectPerformanceRequests = useCallback(() => {
    const yearKey = defaultYear.toString();
    getProjectPerformanceSummary(yearKey, defaultDimDate);
    getProjectPerformanceDetail(yearKey, defaultDimDate);
  }, [defaultYear, defaultDimDate, getProjectPerformanceSummary, getProjectPerformanceDetail]);

  useEffect(() => {
    if (!isMounted.current) return;

    if (!lastFetchedCompany || !lastFetchedDimDate) {
      triggerProjectPerformanceRequests();
    }
  }, [defaultYear, defaultDimDate, lastFetchedCompany, lastFetchedDimDate, triggerProjectPerformanceRequests]);

  useEffect(() => {
    if (!isMounted.current) return;

    const valuesChanged =
      lastFetchedCompany !== selectedCompany ||
      lastFetchedDimDate !== defaultDimDate;

    if (
      valuesChanged &&
      lastFetchedCompany &&
      lastFetchedDimDate
    ) {
      triggerProjectPerformanceRequests();
    }
  }, [defaultDimDate, selectedCompany, defaultYear, lastFetchedCompany, lastFetchedDimDate, triggerProjectPerformanceRequests]);

  return (
    <div style={{ overflowX: 'hidden', overflowY: 'hidden' }}>
      <TopChart
        summaryData={summaryData}
        selectedYears={selectedYears}
        label={label}
        profitType={profitType}
        selectedTopItems={selectedTopItems}
        handleFilter={handleFilter}
        getProjectPerformanceSummary={getProjectPerformanceSummary}
        getProjectPerformanceDetail={getProjectPerformanceDetail}
        topchartHeight={topchartHeight}
        defaultStartMonth={defaultStartMonth}
      />
      <BottomChart
        detailData={detailData}
        selectedYears={selectedYears}
        profitType={profitType}
        profitSubType={profitSubType}
        selectedTopItems={selectedTopItems}
        handleFilter={handleFilter}
        getProjectPerformanceDetail={getProjectPerformanceDetail}
        topchartHeight={topchartHeight}
        showFilter={showFilter}
        sortFilter={sortFilter}
      />

      {bubbleFlag && projectPerformanceSummary && (
        <div
          className="reporting-tooltip"
          style={{
            position: 'fixed',
            bottom: 60,
            right: 30,
            zIndex: 1000,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '1'}
          onMouseLeave={(e) => e.target.style.opacity = '0.5'}
        >
          <Tooltip 
            title={projectPerformanceSummary} 
            position="top-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#2c3e50',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }
              }
            }}
          >
            <InsertCommentIcon color="success" />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

ProjectPerformance.propTypes = {
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  selectedCompany: PropTypes.number,
  projectPerformanceSetting: PropTypes.string,
};

export default ProjectPerformance;
